<template>
  <div>
    <v-dialog
      v-model="visible"
      scrollable
      :persistent="true"
      :width="1500"
      :noClickAnimation="true"
      @click:outside="hide"
    >
      <v-card>
        <v-card-title>
          {{ title }}
        </v-card-title>
        <v-divider />
        <v-card-text class="py-4">
          <v-stepper
            v-model="activeStep"
          >
            <v-stepper-header>
              <template
                v-for="(step, index) in getSteps"
              >
                <v-stepper-step
                  :key="`stepper-step-${index + 1}`"
                  :complete="step.completed"
                  :step="index + 1"
                >
                  {{ step.title }}
                </v-stepper-step>
                <v-divider
                  v-if="index !== getSteps.length - 1"
                  :key="`divider-${index + 1}`"
                />
              </template>
            </v-stepper-header>
            <v-stepper-content
              v-for="(step, index) in getSteps"
              :key="`stepper-content-${index + 1}`"
              :step="index + 1"
            >
              <ValidationObserver
                :ref="`stepperObserver_${index}`"
                v-slot="{ valid, dirty }"
              >
                <form>
                  <component
                    :is="component"
                    :ref="'component_'+index + 1"
                    v-model="componentInstancesInStepperState"
                    v-bind="props"
                    :currentStep="index + 1"
                    @mounted.once="setInitValidationState(index)"
                  />
                  <SlotScopeWatcher
                    :value="valid"
                    @change="updateCompletedSteps(index, $event)"
                  />
                  <SlotScopeWatcher
                    :value="dirty"
                    @change="isFormDirty = $event"
                  />
                </form>
              </ValidationObserver>
            </v-stepper-content>
          </v-stepper>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="hide"
          >
            {{ $t('forms.btn.cancel') }}
          </v-btn>
          <v-btn
            text
            :disabled="parseInt(activeStep) === 1"
            @click="back(parseInt(activeStep) - 1)"
          >
            {{ $t('forms.btn.back') }}
          </v-btn>
          <v-btn
            :disabled="!isStepCompleted(activeStep - 1)"
            color="primary"
            @click="()=>setDone(parseInt(activeStep), parseInt(activeStep) - 1)"
            @keydown.enter.prevent="$event.target.blur()"
          >
            {{ nextButtonLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmModal
      :showConfirmBox="showConfirmBox"
      @hide="showConfirmBox = $event"
      @destroyModal="destroyModal"
    />
  </div>
</template>

<script>
import ConfirmModal from '@/components/shared/forms-controls/ConfirmModal.vue';
import { ValidationObserver } from 'vee-validate';
import modalMixin from '@/mixins/ModalMixin';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import get from 'lodash/get';
import sampleTypes from '@/constants/sampleTypes';
import { mapGetters } from 'vuex';

export default {
    inject: ['$validator'],
    components: {
        ValidationObserver,
        ConfirmModal,
        SlotScopeWatcher,
    },
    mixins: [
        modalMixin,
    ],
    data() {
        return {
            activeStep: null,
            // As the stepper will launch as many component as the amount of steps,
            // a common state for each instance must be kept here
            componentInstancesInStepperState: {},
            props: null,
            isComponentLoading: false,
        };
    },
    computed: {
        ...mapGetters([
            'getSteps',
        ]),
        nextButtonLabel() {
            return parseInt(this.activeStep, 10) === (this.getSteps.length)
                ? this.$t('forms.btn.save')
                : this.$t('forms.btn.next');
        },
    },
    methods: {
        isStepCompleted(activeStepIndex) {
            // quickfix to allow going to next step for deposit sample in edition mode
            if (get(this.props, 'sample.type') === sampleTypes.DEPOSIT && this.props.isEdition) {
                this.$store.dispatch('updateStep', { index: activeStepIndex, valid: true });
            }
            if (activeStepIndex === this.getSteps.length - 1) {
                this.$store.dispatch('updateStep', { index: activeStepIndex, valid: true });
            }
            const activeStep = this.getSteps[activeStepIndex] || {};
            return activeStep.completed;
        },
        updateCompletedSteps(index, valid) {
            if (index === this.activeStep - 1) {
                if (valid === null || valid === undefined) return;
                this.$store.dispatch('updateStep', { index, valid });
            }
        },
        async isFormValid() {
            const validationPromises = this.getSteps.map((step, index) => this.$refs[`stepperObserver_${index}`][0].validate());
            return (await Promise.all(validationPromises)).every(validation => validation);
        },
        async confirm() {
            // Validate the inputs
            if (await this.isFormValid()) {
            // Check if onConfirm is a function, otherwise just close it
                if (typeof this.onConfirm === 'function') {
                    this.onConfirm(this.componentInstancesInStepperState);
                }
                this.destroyModal();
            }
        },
        show(params) {
            // show the modal
            this.visible = true;
            // required
            this.title = params.title;
            this.component = params.component;
            this.editMode = params.isEdition;
            this.activeStep = '1';
            // optional
            this.onConfirm = params.onConfirm;
            // We add the init property to true to notice component instance that this is only the initialization state
            this.componentInstancesInStepperState = { ...params.payload, init: true };
            this.props = params.payload;
            const formatedSteps = Array.from(params.steps).map(step => ({ ...step, completed: this.editMode }));
            this.$store.dispatch('setSteps', formatedSteps);
        },
        async setInitValidationState(index) {
            if (this.editMode) {
                const ref = this.$refs[`component_${index}`];
                if (ref && ref[0].setFlagsToAllValidationProviders) {
                    await ref[0].setFlagsToAllValidationProviders({ dirty: true, valid: true });
                }
            }
        },
        back(previousStep) {
            this.activeStep = previousStep;
        },
        async setDone(nextStepId, activeStep) {
            if (nextStepId === activeStep) return;
            if (this.getSteps[nextStepId]) {
                const ref = this.$refs[`component_${nextStepId}`];
                if (ref && ref[0].setFlagsToAllValidationProviders) {
                    await ref[0].setFlagsToAllValidationProviders({ dirty: this.getSteps[nextStepId].completed });
                }
                this.activeStep = (nextStepId + 1).toString();
            } else {
                if (!(await this.isFormValid())) {
                    for (let i = 0; i < this.steps.length; i += 1) {
                        if (!this.getSteps[i].completed) {
                            this.activeStep = i.toString();
                            return;
                        }
                    }
                }
                this.$store.dispatch('updateStep', { index: activeStep, valid: true });
                await this.confirm();
            }
        },
    },
};
</script>
